import { css, cx } from '@repo/pandacss/styled-system/css';
import type {
  CssVarProperties,
  Nested,
  SystemProperties,
} from '@repo/pandacss/styled-system/types';
import type React from 'react';

type Props = {
  additionalStyles?: string;
  color?: string;
  gap?: `${number}px` | `${number}rem`;
  testid?: string;
  size?: `${number}px` | `${number}rem`;
  value?: number;
};

const emptySvg = `url("data:image/svg+xml;utf8, <svg xmlns='http://www.w3.org/2000/svg' viewBox='2 2 20 19'><path d='M12 17.27 18.18 21l-1.64-7.03L22 9.24l-7.19-.61L12 2 9.19 8.63 2 9.24l5.46 4.73L5.82 21z'/></svg>")`;
const filledSvg = `url("data:image/svg+xml;utf8, <svg xmlns='http://www.w3.org/2000/svg' viewBox='2 2 20 19'><path d='M12 17.27L18.18 21l-1.64-7.03L22 9.24l-7.19-.61L12 2 9.19 8.63 2 9.24l5.46 4.73L5.82 21z'/></svg>")`;

const emptyAndFilledCommonStyles = {
  content: '""',
  position: 'absolute',
  inset: '0',
  WebkitMaskRepeat: 'repeat-x',
  maskRepeat: 'repeat-x',
  WebkitMaskSize:
    'calc(var(--star-rating-size) + var(--star-rating-gap) - 0.5px) var(--star-rating-size)',
  maskSize:
    'calc(var(--star-rating-size) + var(--star-rating-gap) - 0.5px) var(--star-rating-size)',
  WebkitMaskPosition: 'left center',
  maskPosition: 'left center',
} satisfies Nested<SystemProperties & CssVarProperties>;

const spanClass = css({
  position: 'relative',
  display: 'inline-block',
  lineHeight: 'xs',
  fontFamily: '[system-ui, sans-serif]',
  inlineSize:
    '[calc(var(--star-rating-size) * var(--star-rating-max) + (max(var(--star-rating-max) - 1, 0) * var(--star-rating-gap)))]',
  blockSize: 'var(--star-rating-size)',
  fontSize: 'var(--star-rating-size)',
  _before: {
    backgroundColor: 'var(--star-empty)',
    WebkitMaskImage: 'var(--empty-star-svg)',
    maskImage: 'var(--empty-star-svg)',
    ...emptyAndFilledCommonStyles,
  },
  _after: {
    backgroundColor: 'var(--star-fill)',
    inlineSize: 'var(--pct)',
    overflowX: 'hidden',
    overflowY: 'hidden',
    WebkitMaskImage: 'var(--star-svg)',
    maskImage: 'var(--star-svg)',
    ...emptyAndFilledCommonStyles,
  },
  '@supports not (mask-image: var(--star-svg))': {
    _before: {
      content: '"★★★★★"',
      color: 'var(--star-empty)',
      letterSpacing: 'var(--star-rating-gap)',
    },
    _after: {
      content: '"★★★★★"',
      color: 'var(--star-fill)',
      letterSpacing: 'var(--star-rating-gap)',
      position: 'absolute',
      top: '0',
      right: '0',
      bottom: '0',
      left: '0',
      inlineSize: 'var(--pct)',
      overflowX: 'hidden',
      overflowY: 'hidden',
    },
  },
  '@media (forced-colors: active)': {
    _before: {
      backgroundColor: '[CanvasText]',
    },
    _after: {
      backgroundColor: '[Highlight]',
    },
  },
});

export function Rating({
  additionalStyles,
  color = '#008254',
  gap = '0.15rem',
  testid,
  size = '24px',
  value,
}: Props) {
  if (!value) return null;
  return (
    <span
      role="img"
      data-testid={testid}
      aria-label={`${value} out of 5`}
      className={cx(spanClass, additionalStyles)}
      style={
        {
          '--empty-star-svg': emptySvg,
          '--star-svg': filledSvg,
          '--value': value,
          '--star-fill': color,
          '--star-rating-gap': gap,
          '--star-rating-size': size,
          '--star-rating-max': 5,
          '--star-empty': '#ccc',
          '--pct': 'calc(((var(--value) * 100%) / var(--star-rating-max)) + 1px)',
        } as React.CSSProperties
      }
    />
  );
}
